import React from 'react';
import { CardSectionRate } from '../../CardSectionRate/CardSectionRate';
import SectionSubtitle from '@/components/Typography/SectionSubtitle';
import { TextSummaryBox } from '@/components/TextSummaryBox/TextSummaryBox';
import { RatingSection } from '@/api/mappers/mapper-types';
import { MappedPaintAndGapMeasurements } from '@/api/mappers/paint-measurement.mapper';
import { VehiclePaintMeasurement } from '@/components/VehiclePaintMeasurement/VehiclePaintMeasurement';
import { VehiclePaintPoints } from '@/components/VehiclePaintPoints/VehiclePaintPoints';
import { VehicleGapDetection } from '@/components/VehicleGapDetection/VehicleGapDetection';
import { GlassControl } from '@/components/GlassControl/GlassControl';
import { VehicleType } from '@/components/VehiclePaintMeasurement/VehiclePaintMeasurement.types';
import type { CarWindowsSubsection } from '@/components/GlassControl/GlassControl.types';
import { CardSectionRateWide, LayoutSection, SectionShortcutButton, SectionSummary, SectionTitle } from '@/components';
import { TiresControl } from '@/components/TiresControl/TiresControl';
import type { MappedCarTiresSubsection, MappedCarLightsSubsection } from '@/api/mappers/car-body-section.mapper';
import { CarDetailsAssessment } from '@/components/CarDetailsAssessment';
import { PhotoGallery } from '@/components/PhotoGallery';
import type { PhotoGalleryItem } from '@/components/PhotoGallery/PhotoGallery.types';

interface RaportCarBodyProps {
  /** Sekcja oceny grubości lakieru z tytułem, ratingiem i opisem */
  ratingSection: RatingSection;
  /** Lista uwag dotyczących ekspertyzy nadwozia */
  remarks: string[];
  /** Dane pomiarów lakieru i szczelin */
  paintAndGapMeasurements?: MappedPaintAndGapMeasurements | null;
  /** Dane kontroli szyb */
  windowsData?: CarWindowsSubsection | null;
  /** Dane kontroli opon */
  tiresData?: MappedCarTiresSubsection | null;
  /** Dane kontroli świateł */
  lightsData?: MappedCarLightsSubsection | null;
  /** Galeria zdjęć nadwozia */
  galleryPhotos?: PhotoGalleryItem[];
  /** Typ pojazdu */
  vehicleType?: VehicleType;
}

export default function RaportCarBody({
  ratingSection,
  remarks,
  paintAndGapMeasurements,
  windowsData,
  tiresData,
  lightsData,
  galleryPhotos,
  vehicleType = 'sedan',
}: RaportCarBodyProps) {
  const hasGapShortcut = Boolean(paintAndGapMeasurements?.gapPoints && paintAndGapMeasurements.gapPoints.length > 0);
  const hasPaintShortcut = Boolean(paintAndGapMeasurements?.measurementAreas);
  const hasWindowsShortcut = Boolean(windowsData);
  const hasTiresShortcut = Boolean(tiresData && tiresData.tires.length > 0);
  const hasLightsShortcut = Boolean(
    lightsData && lightsData.assessmentItems.length > 0,
  );
  const hasAnyShortcut =
    hasGapShortcut || hasPaintShortcut || hasWindowsShortcut || hasTiresShortcut || hasLightsShortcut;

  return (
    <section className="mt-[100px] bg-white pt-[10px]">
      <SectionTitle
        title="Nadwozie"
        description="Grubość lakieru / Szyby / Ogumienie / Światła"
        icon="car"
        bgWhite={true}
      />

      {hasAnyShortcut && (
        <div className="px-4 mt-10 md:hidden">
          <p className="text-sm font-semibold text-text-primary mb-4">
            Sprawdź raporty dostępne w tej sekcji:
          </p>
          <div className="flex flex-col gap-3">
            {hasGapShortcut && (
              <SectionShortcutButton
                label="Pomiar szczelin"
                targetId="pomiary-szczelin"
              />
            )}
            {hasPaintShortcut && (
              <SectionShortcutButton
                label="Grubość lakieru"
                targetId="pomiary-lakieru"
              />
            )}
            {hasWindowsShortcut && (
              <SectionShortcutButton label="Szyby" targetId="kontrola-szyb" />
            )}
            {hasTiresShortcut && (
              <SectionShortcutButton
                label="Ogumienie"
                targetId="kontrola-ogumienia"
              />
            )}
            {hasLightsShortcut && (
              <SectionShortcutButton
                label="Światła"
                targetId="kontrola-oswietlenia"
              />
            )}
          </div>
        </div>
      )}

      <div id="podsumowanie-ekspertyzy-nadwozia" className={`px-4 mt-10 md:px-[24px] lg:px-[24px] 2xl:px-[48px] lg:max-w-[1364px]`}>
        <SectionSubtitle
          title="Podsumowanie ekspertyzy nadwozia"
          description="Ocena punktowa oraz zestawienie uwag."
        />

        <div className="flex flex-col gap-11 items-start mt-11 md:flex-row">
          {ratingSection.rating !== null && (
            ratingSection.rating === 5 ?
              <CardSectionRateWide
                title={ratingSection.title}
                rating={ratingSection.rating}
                description={'Wygląda na to, że nasza ekspertyza nie wykazała żadnych nieprawidłowości w ocenie nadwozia.'}
                ratingMessage={ratingSection.ratingMessage}
              />
              : <>
                <CardSectionRate
                  title={ratingSection.title}
                  rating={ratingSection.rating || 0}
                  ratingMessage={ratingSection.ratingMessage}
                  description={ratingSection.description ?? undefined}
                />
                {remarks.length > 0 && <TextSummaryBox title="Na to zwróć uwagę:" items={remarks} />}
              </>
          )}

        </div>
      </div>
      {/* Pomiary szczelin (A-J) */}
      <div id="pomiary-szczelin">
        {paintAndGapMeasurements?.gapPoints &&
          paintAndGapMeasurements.gapPoints.length > 0 && (
            <VehicleGapDetection
              vehicleType={vehicleType}
              gapPoints={paintAndGapMeasurements.gapPoints}
              className="mt-12"
            />
          )}
      </div>

      {/* Pomiary lakieru - obszary (1-11) */}
      <div id="pomiary-lakieru">
        {paintAndGapMeasurements?.measurementAreas && (
          <VehiclePaintMeasurement
            vehicleType={vehicleType}
            measurementAreas={paintAndGapMeasurements.measurementAreas}
          />
        )}
      </div>

      {/* Pomiary lakieru - punkty pomiarowe wnęk i komory silnika (12-32) */}
      <div id="pomiary-lakieru-i-wnek">
        {paintAndGapMeasurements?.measurementAreas && (
          <VehiclePaintPoints
            vehicleType={vehicleType}
            measurementAreas={paintAndGapMeasurements.measurementAreas}
          />
        )}
      </div>


      <div className={`px-4 mt-10 md:px-[24px] lg:px-[24px] 2xl:px-[48px]`}>
        <div className="ml-0 max-w-[1268px]">
          <SectionSummary iconName="car" title="Podsumowanie pomiarów szczelin i lakieru" content={paintAndGapMeasurements?.expertSummary || ''} />
        </div>
      </div>

      {/* Kontrola szyb */}
      <div id="kontrola-szyb">
        {windowsData &&
          <LayoutSection>
            <GlassControl data={windowsData} className="mt-12" />
          </LayoutSection>
        }
      </div>

      {/* Kontrola opon */}
      <div id="kontrola-ogumienia">
        {tiresData && tiresData.tires.length > 0 && (
          <LayoutSection>
            <SectionSubtitle
              title="Kontrola ogumienia"
              description="Weryfikacja ogumienia – ocena stanu opon i felg."
            />
            <div className="mt-10">
              <TiresControl
                tires={tiresData.tires}
                hasSecondSet={tiresData.hasSecondSet}
                expertSummary={tiresData.expertSummary || undefined}
              />
            </div>
          </LayoutSection>
        )}
      </div>

      {/* Kontrola oświetlenia */}
      <div id="kontrola-oswietlenia">
        {lightsData && lightsData.assessmentItems.length > 0 && (
          <div className="mt-12 px-4 md:px-[24px] lg:px-[24px] 2xl:px-[48px] max-w-[1364px]">
            <SectionSubtitle
              title="Kontrola oświetlenia"
              description="Weryfikacja oświetlenia – sprawdzenie sprawności świateł."
            />
            <div className="mt-10">
              <CarDetailsAssessment
                assessmentItems={lightsData.assessmentItems}
                summaryTitle="Podsumowanie Rzeczoznawcy dotyczące kontroli oświetlenia"
                summaryContent={lightsData.expertSummary || undefined}
                summaryIcon='airplane'
              />
            </div>
          </div>
        )}
      </div>

      {/* Galeria nadwozia */}
      {galleryPhotos && galleryPhotos.length > 0 && (
        <div id="galeria-nadwozia" className="mt-4 lg:mt-12 px-4 md:px-[24px] lg:px-[24px] 2xl:px-[48px] max-w-[1364px]">
          <PhotoGallery
            photos={galleryPhotos}
            title="Galeria nadwozia pojazdu"
            iconName="car"
            collapsible={true}
            defaultExpanded={false}
            previewCount={4}
            galleryVariant="default"
          />
        </div>
      )}
    </section>
  );
}
